import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import Select from "react-select";
import CustomInput from '../../../components/common/CustomInput'
import CustomTextarea from '../../../components/common/CustomTextarea'
import CustomFile from '../../../components/common/CustomFile'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import CustomSelect from '../../../components/common/CustomSelect'

// validation
const validationSchema = Yup.object({
  full_name: Yup.string().trim().required('Full Name is Required'),
  email: Yup.string()
    .trim()
    .email('Invalid email format')
    .required('Email is Required'),
  mobile: Yup.string()
    .trim()
    .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits')
    .required('Mobile No. is Required'),
  skype: Yup.string().trim().required('Skype is Required'),
  address: Yup.string().trim().required('Address is Required'),
  work_experience: Yup.string().trim().required('Work Experience is Required'),
  candidate_role: Yup.string().trim().required('Candidate Role is Required'),
  // photo: Yup.string().trim().required('Photo is Required'),
  // id_proof: Yup.string().trim().required('Id Proof is Required'),
  username: Yup.string().trim().required('Username is Required'),
  // password: Yup.string().trim().required('Password is Required'),
  // confirm_password: Yup.string()
  //   .trim()
  //   .oneOf([Yup.ref('password'), null], 'Password Must Be Same')
  //   .required('Confirm Password is Required'),
})

const reactSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: '8px',
    padding: '5px',
    boxShadow: 'none',
  }),
};


function Edit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [RoleOptions, setRoleOptions] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [leaders, setLeaders] = useState([])
  const [selectedLeaders, setSelectedLeaders] = useState([])

  const fetchRoleData = async () => {
    try {
      const response = await axiosAuthInstance.get('role/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setRoleOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchRoleData()
  }, [])

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const formik = useFormik({
    initialValues: {
      full_name: '',
      email: '',
      mobile: '',
      skype: '',
      address: '',
      work_experience: '',
      team_leader: '',
      candidate_role: '',
      photo: null,
      id_proof: null,
      username: '',
      password: '',
      confirm_password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  const fetchData = async () => {
    try {
      const response = await axiosAuthInstance.get(`team/detail/${id}`)
      if (response.data && response.data.result) {
        const teamData = response.data.result

        formik.setValues({
          full_name: teamData.full_name,
          email: teamData.email,
          mobile: teamData.mobile,
          skype: teamData.skype,
          address: teamData.address,
          work_experience: teamData.work_experience,
          team_leader: teamData.team_leader?.map((item) => item._id),
          candidate_role: teamData.candidate_role._id,
          photo: teamData.photo,
          id_proof: teamData.id_proof,  
          username: teamData.username,
          // password: teamData.password,
          // confirm_password: teamData.confirm_password,
        })
        setSelectedLeaders(teamData.team_leader?.map((item) => ({
          value: item._id,
          label: item.full_name,
        })))
        setLoading(false)
      }
    } catch (error) {
      console.log('error in fetching edit data: ', error)
    }
  }

  const fetchTeamLeaders = async () => {
    try {
      const response = await axiosAuthInstance.get('team/dropdown', { roleFilter: true })
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.full_name} (${item.role})`,
        }))
        setLeaders(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchData()
    fetchTeamLeaders()
  }, [id])

  const handleSubmit = async (values) => {
    setIsSubmitted(true)
    try {
      const formData = new FormData()
      formData.append('photo', values.photo)
      formData.append('id_proof', values.id_proof)
      formData.append('full_name', values.full_name)
      formData.append('email', values.email)
      formData.append('mobile', values.mobile)
      formData.append('skype', values.skype)
      formData.append('address', values.address)
      formData.append('work_experience', values.work_experience)
      formData.append('team_leader', JSON.stringify(values.team_leader))
      formData.append('candidate_role', values.candidate_role)
      formData.append('username', values.username)

      const response = await axiosAuthInstance.put(
        `team/update/${id}`,
        formData,
      )
      if (response.status === 200) {
        navigate('/team')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSubmitted(false)
    }
  }

  const handleLeaderChange = (selectedOptions) => {
    setSelectedLeaders(selectedOptions);
    const selectedIds = selectedOptions.map(option => option.value);
    formik.setFieldValue('team_leader', selectedIds);
  };

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <FormikProvider value={formik}>
      <form
        className='flex flex-col p-4 gap-4'
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Field
            name='full_name'
            label=' Full Name'
            placeholder='Enter Your Full Name'
            required
            component={CustomInput}
          />
          <Field
            name='email'
            label='Email'
            placeholder='Enter Your Email '
            required
            component={CustomInput}
          />
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Field
            name='mobile'
            label='Mobile No.'
            placeholder='Enter Your Mobile No.'
            type='number'
            required
            component={CustomInput}
          />
          <Field
            name='skype'
            label='Skype'
            placeholder='Enter Your Skype '
            required
            component={CustomInput}
          />
        </div>

        <Field
          name='address'
          label='Address'
          placeholder='Enter Your Address'
          required
          component={CustomTextarea}
        />

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Field
            name='work_experience'
            label='Work Experience'
            placeholder='Enter Your Experience'
            type='number'
            required
            component={CustomInput}
          />
          <Field
            name='team_leader'
            label='Team Leader'
            placeholder='Enter Your Team Leader'
            required
            component={CustomSelect}
            options={leaders}
            isMulti
          />

        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Field
            name='candidate_role'
            label='Candidate Role'
            placeholder='Enter Your Role'
            required
            component={CustomSelect}
            options={RoleOptions}
          />
          <Field
            name='photo'
            accept='image/*'
            label='Photo'
            component={CustomFile}
            url={formik.values.photo}
          />
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Field
            name='id_proof'
            accept='image/*'
            label='Id_Proof'
            component={CustomFile}
            url={formik.values.id_proof}
          />
          <Field
            name='username'
            label='Username'
            placeholder='Enter Your Username'
            required
            component={CustomInput}
          />
        </div>

        {/* <div className='grid grid-cols-1 md:grid-cols-2 gap-6 '>
          <div className='relative '>
            <Field
              name='password'
              label='Password'
              placeholder='Enter Your Password'
              required
              component={CustomInput}
              type={showPassword ? 'text' : 'password'}
            />
            <div
              className='absolute inset-y-0 pt-5 right-4 flex items-center cursor-pointer'
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </div>
          </div>

          <div className=' relative '>
            <Field
              name='confirm_password'
              label='Confirm Password'
              placeholder='Enter Your Confirm Password'
              required
              component={CustomInput}
              type={showConfirmPassword ? 'text' : 'password'}
            />
            <div
              className='absolute inset-y-0 pt-5 right-4 flex items-center cursor-pointer'
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
            </div>
          </div>
        </div> */}
        <button
          type='submit'
          disabled={isSubmitted}
          className='bg-primary text-xl font-medium text-white rounded-lg px-6 py-2'
        >
          {isSubmitted ? (
            <div className='w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent'></div>
          ) : ('Update')}
        </button>
      </form>
    </FormikProvider>
  )
}

export default Edit
